import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { api, type RecurringChain, type RecurringJob } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import { TaskTable } from "../../components/dashboard/platform/DurableTasksPanel.tsx";
import JobName from "../../components/dashboard/platform/JobName.tsx";
import {
  cadenceLabel,
  elapsed,
  jobTitle,
  recurringStatus,
  seconds,
  timeUntil,
} from "../../components/dashboard/platform/recurringFormat.ts";
import DetailDrawer from "../../components/DetailDrawer.tsx";
import RelativeTime from "../../components/RelativeTime.tsx";
import SectionHeading from "../../components/SectionHeading.tsx";
import Sparkline from "../../components/Sparkline.tsx";
import StatusChip from "../../components/StatusChip.tsx";
import { StatusLabel } from "../../components/StatusIcon.tsx";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import { formatNumber, humanize } from "../../utils/format.ts";

const num = { fontVariantNumeric: "tabular-nums" } as const;

/**
 * One recurring job: its runs per hour over the week, each chain (a CRM
 * connection, or the one `main` chain) with the task its tick waits on, and
 * the latest ticks.
 */
export default function RecurringJobDrawer({
  job,
  onClose,
  onStep,
}: {
  job: RecurringJob | null;
  onClose: () => void;
  onStep: (direction: 1 | -1) => void;
}) {
  const status = job ? recurringStatus(job.status) : null;
  return (
    <DetailDrawer
      open={!!job}
      onClose={onClose}
      onStep={onStep}
      overline={job ? `${job.category_label} · ${humanize(job.group)} · ${cadenceLabel(job)}` : undefined}
      title={job ? <JobName country={job.country}>{jobTitle(job)}</JobName> : ""}
      actions={
        job &&
        status && (
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <StatusLabel kind={status.kind} label={status.label} />
            {job.switch_key && (
              <Button size="small" variant="outlined" component={RouterLink} to="/settings/background-jobs">
                {job.switch_on ? "Switch off" : "Switch on"} in background jobs
              </Button>
            )}
          </Stack>
        )
      }
    >
      {job && <DrawerBody job={job} />}
    </DetailDrawer>
  );
}

function DrawerBody({ job }: { job: RecurringJob }) {
  const ticks = useQuery({
    queryKey: keys.platform.recurringTicks(job.name, null),
    queryFn: () => api.getPlatformRecurringTicks(job.name, null),
    meta: { live: true },
  });
  const tasks = job.chains.flatMap((c) => (c.task ? [c.task] : []));
  const labels = new Map(job.chains.map((c) => [c.key, c.label]));
  const week = job.hourly.map((h) => h.ran + h.failed);

  return (
    <Stack spacing={3}>
      {job.description && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {job.description}
        </Typography>
      )}
      <Stack direction="row" spacing={4}>
        <Figure label="Runs, 24h" value={formatNumber(job.runs_24h)} />
        <Figure label="Failed, 24h" value={job.failures_24h ? formatNumber(job.failures_24h) : "–"} />
        <Figure label="Typical run" value={seconds(job.p50_s)} />
        <Figure label="Slowest 5%" value={seconds(job.p95_s)} />
      </Stack>
      <Box>
        <SectionHeading title="Runs per hour, last 7 days" />
        <Sparkline values={week} width={560} height={64} variant="area" />
      </Box>

      <Box>
        <SectionHeading title={job.keyed ? "Chains" : "Chain"} count={job.keyed ? job.chains.length : undefined} />
        <ChainTable chains={job.chains} keyed={job.keyed} />
      </Box>

      {tasks.length > 0 && (
        <Box>
          <SectionHeading title="Waiting on" count={tasks.length} />
          <TaskTable tasks={tasks} />
        </Box>
      )}

      <Box>
        <SectionHeading title="Latest ticks" count={ticks.data?.ticks.length} />
        {ticks.isLoading ? (
          <CenteredSpinner />
        ) : ticks.data?.ticks.length ? (
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                {job.keyed && <TableCell>Chain</TableCell>}
                <TableCell>Outcome</TableCell>
                <TableCell align="right">Took</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticks.data.ticks.map((t) => (
                <TableRow key={`${t.key}-${t.started_at}`}>
                  <TableCell>
                    <RelativeTime value={t.started_at} />
                  </TableCell>
                  {job.keyed && <TableCell>{labels.get(t.key) ?? t.key}</TableCell>}
                  <TableCell>
                    <Tooltip title={t.error ?? ""} disableHoverListener={!t.error}>
                      <span>
                        <StatusChip kind="tick" value={t.outcome} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {seconds(t.duration_s)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
            No ticks in the last 7 days
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={num}>
        {value}
      </Typography>
    </Box>
  );
}

function ChainTable({ chains, keyed }: { chains: RecurringChain[]; keyed: boolean }) {
  return (
    <ScrollTable>
      <TableHead>
        <TableRow>
          {keyed && <TableCell>Key</TableCell>}
          <TableCell>Status</TableCell>
          <TableCell>Last run</TableCell>
          <TableCell>Next run</TableCell>
          <TableCell align="right">Runs, 24h</TableCell>
          <TableCell align="right">Last took</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {chains.map((c) => {
          const status = recurringStatus(c.status);
          return (
            <TableRow key={c.key}>
              {keyed && <TableCell>{c.label}</TableCell>}
              <TableCell>
                <Tooltip title={c.last_error ?? ""} disableHoverListener={!c.last_error}>
                  <Box component="span">
                    <StatusLabel kind={status.kind} label={status.label} />
                  </Box>
                </Tooltip>
                {c.running_since && (
                  <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                    Running for {elapsed(c.running_since)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <RelativeTime value={c.last_run_at} />
              </TableCell>
              <TableCell>
                {c.status === "stopped"
                  ? "–"
                  : !c.next_run_at && c.running_since
                    ? "After this run"
                    : timeUntil(c.next_run_at)}
              </TableCell>
              <TableCell align="right" sx={num}>
                {formatNumber(c.runs_24h)}
                {c.failures_24h > 0 && (
                  <Typography variant="caption" component="div" sx={{ color: "error.main" }}>
                    {formatNumber(c.failures_24h)} failed
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={num}>
                {seconds(c.last_duration_s)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </ScrollTable>
  );
}
